import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteCandidateJobNoteMutation } from '@/modules/candidates/mutations/useDeleteCandidateJobNoteMutation';
import { useUpdateCandidateJobNoteMutation } from '@/modules/candidates/mutations/useUpdateCandidateJobNoteMutation';
import { DroppedNoteLinks } from '@/modules/notes/components/DroppedNoteLinks';
import { NoteCard } from '@/modules/notes/components/NoteCard';
import type {
  CandidateJobNoteType,
  NoteFormData,
} from '@/modules/notes/components/NoteForm';
import { Button } from '@/modules/ui/components/button/Button';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import {
  type CandidateJobNote,
  candidateJobNoteTypeOptions,
} from './CandidateJobNotesList';

interface CandidateJobNoteCardProps {
  note: CandidateJobNote;
  candidateJobId: string;
  canEdit?: boolean;
  defaultType?: CandidateJobNoteType;
}

export const CandidateJobNoteCard = ({
  note,
  candidateJobId,
  canEdit,
  defaultType,
}: CandidateJobNoteCardProps) => {
  const [selectedVersion, setSelectedVersion] = useState<'polished' | 'raw'>(
    'polished',
  );

  const deleteNoteMutation = useDeleteCandidateJobNoteMutation();
  const updateNoteMutation = useUpdateCandidateJobNoteMutation();

  const hasRawContent =
    note.type === 'Prescreen' && Boolean(note.rawContent?.trim());

  const handleVersionChange = (version: 'polished' | 'raw') => {
    setSelectedVersion(version);
  };

  const showRaw = selectedVersion === 'raw' && hasRawContent;
  const displayContent = showRaw ? note.rawContent! : note.content;

  const fullName = [note.author?.firstName, note.author?.lastName]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ');
  const author = fullName || note.author?.email?.trim() || null;

  const handleUpdate = (data: NoteFormData) => {
    updateNoteMutation.mutate(
      {
        candidateJobId,
        noteId: note.id,
        body: {
          content: data.content,
          type: data.type as 'General' | 'Prescreen',
        },
      },
      {
        onSuccess: () => {
          toast.success('Note updated successfully');
        },
        onError: (error) => {
          toast.error(`Failed to update note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleDelete = (noteId: string) => {
    deleteNoteMutation.mutate(
      { candidateJobId, noteId },
      {
        onSuccess: () => {
          toast.success('Note deleted successfully');
        },
        onError: (error) => {
          toast.error(`Failed to delete note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  return (
    <NoteCard
      note={note}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isUpdating={updateNoteMutation.isPending}
      typeOptions={candidateJobNoteTypeOptions}
      defaultType={defaultType}
      canEdit={canEdit}
      editMenuLabel={hasRawContent ? 'Edit AI-polished version' : undefined}
      author={author}
      displayContent={displayContent}
      contentFooter={
        hasRawContent && !showRaw ? (
          <DroppedNoteLinks
            original={note.rawContent!}
            polished={note.content}
          />
        ) : null
      }
      headerControls={
        hasRawContent ? (
          <Button
            type="button"
            variant="link"
            className="text-muted-foreground h-auto p-0 text-xs"
            onClick={() =>
              handleVersionChange(
                selectedVersion === 'polished' ? 'raw' : 'polished',
              )
            }
          >
            {selectedVersion === 'polished'
              ? 'View original (read-only)'
              : 'View AI-polished'}
          </Button>
        ) : null
      }
    />
  );
};
